import { Slider, TextField } from '@frameio/vapor';
import { useLocalOptimism } from '@frameio/vapor-migration/hooks';
import { SliderContainer } from '../../sharedStyles';

type OpacityControlProps = {
  value: number;
  onChange: (value: number) => void;
};

// OpacityControl: Allows users to adjust opacity value using a slider and input field
export const OpacityControl: React.FC<OpacityControlProps> = ({
  value,
  onChange,
}) => {
  const [inputValue, setInputValue] = useLocalOptimism(value.toString());

  // Handle slider value changes
  const handleSliderChange = (newValue: number) => {
    const roundedValue = Math.round(newValue);
    onChange(newValue);
    setInputValue(roundedValue.toString());
  };

  // Handle manual input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = Math.round(
      parseFloat(e.target.value.replace(/[^0-9.-]/g, ''))
    );
    setInputValue(isNaN(newValue) ? '0' : newValue.toString());
  };

  // Validate and update value when input loses focus
  const handleInputBlur = () => {
    let newValue = parseInt(inputValue, 10);
    if (isNaN(newValue) || newValue < 10) {
      newValue = 10;
    } else if (newValue > 100) {
      newValue = 100;
    }
    onChange(newValue);
    setInputValue(newValue.toString());
  };

  // Handle Enter key press in input field
  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleInputBlur();
    }
  };

  return (
    <SliderContainer>
      <Slider
        max={100}
        min={10}
        onChange={(v) => handleSliderChange(v)}
        showTooltip={(v) => `${v.toFixed(0)}%`}
        style={{ flex: 1 }}
        value={value}
      />
      <TextField
        onBlur={handleInputBlur}
        onChange={handleInputChange}
        onKeyDown={handleInputKeyDown}
        style={{ maxWidth: '50px' }}
        type="text"
        value={`${inputValue}%`}
      />
    </SliderContainer>
  );
};
